import { useSession, useTenant } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';
import { getMyEmployeePortalEffectiveFeatures } from '@/services/bff/employee-portal.api';
import { getMyAuthorizedResources } from '@/services/bff/permission.api';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from './adapter';
import { getEffectiveAuthorizedResourceAccess } from './devBypass';
import {
  type EmployeePortalEffectiveFeatures,
  getEffectiveEmployeePortalAccess,
} from './employeePortalFeatures';

export type EmployeePortalStatus = 'loading' | 'error' | 'unbound' | 'ready';
type PortalState = {
  status: EmployeePortalStatus;
  access: AuthorizedResourceAccessMap;
  features?: EmployeePortalEffectiveFeatures;
  retry: () => void;
};
const EmployeePortalContext = React.createContext<PortalState>({
  status: 'loading',
  access: {},
  retry: () => undefined,
});

export const useEmployeePortal = () => React.useContext(EmployeePortalContext);

export const EmployeePortalProvider: React.FC<{
  setInitialState: (updater: any) => void;
  permissionRetry?: number;
  children?: React.ReactNode;
}> = ({ setInitialState, permissionRetry = 0, children }) => {
  const { currentUser, loading } = useSession();
  const { currentTenantUuid } = useTenant();
  const scope =
    currentUser && !loading && currentUser.memberUuid
      ? `${currentUser.memberUuid}|${currentTenantUuid || currentUser.tenantUuid || ''}`
      : '';
  const [attempt, setAttempt] = React.useState(0);
  const [state, setState] = React.useState<{
    scope: string;
    status: EmployeePortalStatus;
    access: AuthorizedResourceAccessMap;
    features?: EmployeePortalEffectiveFeatures;
  }>({ scope: '', status: 'loading', access: {} });
  const retry = React.useCallback(() => setAttempt((value) => value + 1), []);

  React.useEffect(() => {
    let active = true;
    setState({ scope, status: 'loading', access: {} });
    setInitialState((previous: any) =>
      previous
        ? {
            ...previous,
            authorizedResourceAccess: {},
            employeePortalFeatures: undefined,
          }
        : previous,
    );
    if (!scope)
      return () => {
        active = false;
      };
    void Promise.all([
      getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
      getMyEmployeePortalEffectiveFeatures(),
    ])
      .then(([resources, features]) => {
        if (!active) return;
        if (
          typeof features?.bound !== 'boolean' ||
          !features.features ||
          typeof features.features !== 'object'
        ) {
          throw new Error('invalid_employee_portal_features');
        }
        const authorizedResourceAccess = getEffectiveAuthorizedResourceAccess(
          buildAuthorizedResourceAccessMap(resources),
        );
        setInitialState((previous: any) =>
          previous
            ? {
                ...previous,
                authorizedResourceAccess,
                employeePortalFeatures: features,
              }
            : previous,
        );
        setState({
          scope,
          status: features.bound ? 'ready' : 'unbound',
          features,
          access: getEffectiveEmployeePortalAccess(
            authorizedResourceAccess,
            features,
          ),
        });
      })
      .catch(() => {
        if (!active) return;
        setState({ scope, status: 'error', access: {} });
      });
    return () => {
      active = false;
    };
  }, [scope, attempt, permissionRetry, setInitialState]);

  const value =
    scope && state.scope === scope
      ? { ...state, retry }
      : { status: 'loading' as const, access: {}, retry };
  return (
    <EmployeePortalContext.Provider value={value}>
      {children}
    </EmployeePortalContext.Provider>
  );
};
